'use client';

import React from 'react';
import gsap from 'gsap';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import styles from './ImageMarqueeSection.module.css';

const MARQUEE_IMAGES = [
  'web_v2/ring-pro/landing-page/lifestyle-images/1.png',
  'web_v2/ring-pro/landing-page/lifestyle-images/2.png',
  'web_v2/ring-pro/landing-page/lifestyle-images/3.png',
  'web_v2/ring-pro/landing-page/lifestyle-images/4.png',
  'web_v2/ring-pro/landing-page/lifestyle-images/5.png',
  'web_v2/ring-pro/landing-page/lifestyle-images/6.png',
  'web_v2/ring-pro/landing-page/lifestyle-images/7.png',
  'web_v2/ring-pro/landing-page/lifestyle-images/8.png',
];

const ImageMarqueeSection: React.FC = () => {
  const gsapContext = useGsapContext();

  const horizontalLoop = (items: HTMLElement[], config: { repeat?: number; paused?: boolean; speed?: number; paddingRight?: number; reversed?: boolean }) => {
    gsapContext.add(() => {
      config = config || {};
      const tl = gsap.timeline({
        repeat: config.repeat,
        paused: config.paused,
        defaults: { ease: 'none' },
        onReverseComplete: () => {
          tl.totalTime(tl.rawTime() + tl.duration() * 100);
        },
      });
      const length = items.length;
      const startX = items[0].offsetLeft;
      const times: number[] = [];
      const widths: number[] = [];
      const xPercents: number[] = [];
      const pixelsPerSecond = (config.speed || 1) * 100;
      const snap = (v: number) => v;
      let curX: number;
      let distanceToStart: number;
      let distanceToLoop: number;

      gsap.set(items, {
        xPercent: (i, el) => {
          const w = (widths[i] = parseFloat(
            gsap.getProperty(el, 'width', 'px') as string,
          ));
          xPercents[i] = snap(
            (parseFloat(gsap.getProperty(el, 'x', 'px') as string) / w) * 100 +
              (gsap.getProperty(el, 'xPercent') as number),
          );
          return xPercents[i];
        },
      });

      gsap.set(items, { x: 0 });

      const totalWidth =
        items[length - 1].offsetLeft +
        (xPercents[length - 1] / 100) * widths[length - 1] -
        startX +
        items[length - 1].offsetWidth *
          (gsap.getProperty(items[length - 1], 'scaleX') as number) +
        (parseFloat(config.paddingRight?.toString() || '0') || 0);

      for (let i = 0; i < length; i++) {
        const item = items[i];
        curX = (xPercents[i] / 100) * widths[i];
        distanceToStart = item.offsetLeft + curX - startX;
        distanceToLoop =
          distanceToStart +
          widths[i] * (gsap.getProperty(item, 'scaleX') as number);

        tl.to(
          item,
          {
            xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
            duration: distanceToLoop / pixelsPerSecond,
          },
          0,
        )
          .fromTo(
            item,
            {
              xPercent: snap(
                ((curX - distanceToLoop + totalWidth) / widths[i]) * 100,
              ),
            },
            {
              xPercent: xPercents[i],
              duration:
                (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
              immediateRender: false,
            },
            distanceToLoop / pixelsPerSecond,
          )
          .add('label' + i, distanceToStart / pixelsPerSecond);
        times[i] = distanceToStart / pixelsPerSecond;
      }

      tl.progress(1, true).progress(0, true);

      if (config.reversed) {
        tl.vars.onReverseComplete?.();
        tl.reverse();
      }

      return tl;
    });
  };

  useIsomorphicLayoutEffect(() => {
    setTimeout(() => {
      const items = gsap.utils.toArray<HTMLElement>('.marquee-slide');
      if (items.length === 0) return;
      horizontalLoop(items, {
        repeat: -1,
        paused: false,
        speed: 1,
        paddingRight: 16,
      });
    }, 1000);
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.marqueeContainer}>
        <div className={styles.track}>
          {MARQUEE_IMAGES.map((src, i) => (
            <div key={i} className={`marquee-slide ${styles.slide}`}>
              <CustomImage
                className={styles.slideImage}
                src={src}
                alt={`Ring PRO lifestyle ${i + 1}`}
                fill
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImageMarqueeSection;
